import {useEffect, useState, useRef} from "react";
import {Link, useNavigate} from "react-router-dom";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Badge} from "@/components/ui/badge";
import {Input} from "@/components/ui/input";
import {Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar";
import Layout from "@/components/Layout";
import LoginDialog from "@/components/LoginDialog";
import CreateTopicDialog from "@/components/CreateTopicDialog";
import { useCommunityCategories } from "@/hooks/useCommunityCategories";
import {
  ArrowRight,
  Clock,
  Eye,
  Lock,
  LogIn,
  MessageSquare,
  Pin,
  Search,
  Star,
  TrendingUp,
  UserCheck,
  Users,
  X,
  RefreshCw,
  Loader2
} from "lucide-react";
import {communityApi} from "@/api";

const Community = () => {
  const [statistics, setStatistics] = useState({
    userCount: 0,
    totalTopicCount: 0,
    monthTopicCount: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [topicsList, setTopicsList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("-1");
  const [sortBy, setSortBy] = useState("updateTime");
  const navigate = useNavigate();
  
  // 分页相关状态
  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(10); // 每页加载 10 条
  const [total, setTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false); // 首次加载状态
  const [hasError, setHasError] = useState(false); // 错误状态
  const listEndRef = useRef<HTMLDivElement>(null);
  
  // 使用社区分类 Hook
  const { categories, isLoading: isCategoriesLoading } = useCommunityCategories();
  
  // Token 验证相关状态
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  // 发起讨论弹窗状态
  const [isCreateTopicOpen, setIsCreateTopicOpen] = useState(false);

  // 搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms 防抖延迟

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        // TODO: 这里可以调用接口验证 token 是否有效
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setIsLoginDialogOpen(true);
      }
      
      setIsCheckingAuth(false);
    };

    checkAuth();
  }, []);

  // 登录成功回调
  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setIsLoginDialogOpen(false);
  };

  // 获取社区统计信息
  const getStatistics = async () => {
    try {
      const response = await communityApi.getTopicStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error("Error fetching getStatistics:", error);
    }
  }
  useEffect(() => {
    getStatistics();
  }, []);

  // 点击发起讨论按钮
  const handleCreateTopic = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!token) {
      // 未登录，弹出登录窗口
      setIsLoginDialogOpen(true);
      return;
    }
    
    // 已登录，打开发起讨论弹窗
    setIsCreateTopicOpen(true);
  };

  // 获取社区讨论列表
  const fetchTopicList = async (isLoadMore = false) => {
    try {
     setHasError(false);
     if (!isLoadMore) {
        setIsLoading(true);
        // 首次加载或筛选条件变化，重置页码和数据
       setPageNum(1);
       setTopicsList([]);
       setHasMore(true);
      } else {
        setIsLoadingMore(true);
      }
     
     const currentPage = isLoadMore ? pageNum + 1 : 1;
      
     const response = await communityApi.getCommunities({
        topicTypeId: selectedCategory,
        title: debouncedSearchTerm,
        orderByColumn: sortBy,
        isAsc: 'desc',
        pageNum: currentPage,
        pageSize: pageSize
      });
      
     const newTopics = response.rows || [];
    const totalCount = response.total || 0;
     setTotal(totalCount);
      
    if (isLoadMore) {
        // 加载更多时追加数据
       setTopicsList(prev => {
        const updatedList = [...prev, ...newTopics];
         // 判断是否还有更多数据
        setHasMore(updatedList.length < totalCount);
         return updatedList;
       });
       setPageNum(currentPage);
      } else {
        // 首次加载直接设置数据
       setTopicsList(newTopics);
        // 判断是否还有更多数据
       setHasMore(newTopics.length < totalCount);
       setPageNum(1);
      }
     setIsLoadingMore(false);
     setIsLoading(false);
    } catch (error) {
     console.error("Error fetching communities:", error);
     setIsLoadingMore(false);
     setIsLoading(false);
     setHasError(true);
   }
  };
  useEffect(() => {
    fetchTopicList(false);
  }, [selectedCategory, debouncedSearchTerm, sortBy]);

  const getCategoryInfo = (categoryId: string) => {
    return categories.find(cat => Number(cat.dictValue) === Number(categoryId)) || categories[0];
  };

  /** 用户角色 1：普通用户 2：官方 */
  const getRoleColor = (role: number) => {
    switch (role) {
      case 1:
        return "bg-gray-100 text-gray-700";
      case 2:
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-green-100 text-green-700";
    }
  };

  // 滚动加载更多
  const handleScroll = () => {
   const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
   const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
   const clientHeight = document.documentElement.clientHeight || window.innerHeight;
    
    // 距离底部还有 100px 时触发加载
   if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore && !isLoadingMore) {
      setIsLoadingMore(true);
      fetchTopicList(true);
    }
  };
  
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, isLoadingMore, pageNum]);
  
  // 点击加载更多按钮
  const handleLoadMore = () => {
   if (hasMore && !isLoadingMore) {
      setIsLoadingMore(true);
      fetchTopicList(true);
    }
  };

  // 加载中状态
  if (isCheckingAuth) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">验证中...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // 未登录状态 - 显示提示页面
  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-20">
          <Card className="max-w-2xl w-full mx-auto">
            <CardContent className="p-12 text-center">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <UserCheck className="w-12 h-12 text-blue-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                请先登录
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                您需要登录后才能访问光智云社区，与全球光学仿真专家交流经验。
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Button onClick={() => setIsLoginDialogOpen(true)} size="lg">
                  <LogIn className="mr-2 w-4 h-4" />
                  立即登录
                </Button>
                <Button variant="outline" onClick={() => navigate('/')} size="lg">
                  返回首页
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 登录弹窗 */}
        <LoginDialog
          isOpen={isLoginDialogOpen}
          onClose={() => setIsLoginDialogOpen(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              欢迎来到光智云社区
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              与全球光学仿真专家交流经验，分享技术心得，共同推进光学仿真技术的发展
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="搜索讨论话题、技术问题..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-12 py-3 text-lg"
              />
              {/* 清空按钮 */}
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            {/* 搜索状态提示 */}
            {debouncedSearchTerm && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                搜索 "<span className="font-medium text-gray-700">{debouncedSearchTerm}</span>" 共找到 {total} 条结果
              </p>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">讨论分类</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-1">
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <button
                        key={category.dictValue}
                        onClick={() => setSelectedCategory(category.dictValue)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                          selectedCategory === category.dictValue
                            ? "bg-blue-50 text-blue-600 border-r-2 border-blue-600"
                            : "text-gray-700"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <IconComponent className="w-4 h-4" />
                            <span className="font-medium text-sm">{category.dictLabel}</span>
                          </div>
                          {/*<Badge variant="secondary" className="text-xs">*/}
                          {/*  {category.count || 0}*/}
                          {/*</Badge>*/}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Community Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">社区统计</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-gray-600">活跃用户</span>
                  </div>
                  <span className="font-semibold">{statistics.userCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-600">讨论话题</span>
                  </div>
                  <span className="font-semibold">{statistics.totalTopicCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4 text-purple-500" />
                    <span className="text-sm text-gray-600">本月新增</span>
                  </div>
                  <span className="font-semibold">{statistics.monthTopicCount}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Category Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${getCategoryInfo(selectedCategory).color}`}>
                    {(() => {
                      const IconComponent = getCategoryInfo(selectedCategory).icon;
                      return <IconComponent className="w-4 h-4" />;
                    })()}
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {getCategoryInfo(selectedCategory).dictLabel}
                    </h2>
                    <p className="text-gray-500">
                      {total} 个话题
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => fetchTopicList(false)}
                    disabled={isLoading}
                    title="刷新列表"
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button onClick={handleCreateTopic}>
                    发起讨论
                  </Button>
                </div>
              </div>

              {/* Sort Options */}
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">排序方式:</span>
                <div className="flex space-x-2">
                  {[
                    { id: "updateTime", name: "最新" },
                    { id: "readCount", name: "热门" },
                    // { id: "commentCount", name: "回复最多" }
                  ].map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setSortBy(option.id)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        sortBy === option.id
                          ? "bg-blue-100 text-blue-700"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Topics List */}
            <div className="space-y-4">
              {/* 加载状态 */}
              {isLoading && (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">加载中...</p>
                </div>
              )}
              
              {/* 错误状态 */}
              {hasError && !isLoading && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">加载失败</h3>
                  <p className="text-gray-500 mb-4">
                    网络异常，请稍后重试
                  </p>
                  <Button onClick={() => fetchTopicList(false)}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    重新加载
                  </Button>
                </div>
              )}
              
              {/* 话题列表 */}
              {!isLoading && !hasError && topicsList.map((topic, topicIndex) => {
                const categoryInfo = getCategoryInfo(topic.topicTypeId);
                
                return (
                  <Card key={`${topic.id}-${topicIndex}`}
                        className="hover:shadow-md transition-shadow duration-200 cursor-pointer"
                        onClick={() => navigate(`/community/topic/${topic.id}`)}>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        {/* Avatar */}
                        <Avatar className="w-10 h-10 flex-shrink-0">
                          {/*<AvatarImage src={topic.author.avatar} />*/}
                          <AvatarFallback>
                            {topic.nickname.charAt(0)}
                          </AvatarFallback>
                        </Avatar>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              {topic.isPinned && (
                                <Pin className="w-4 h-4 text-red-500" />
                              )}
                              {topic.isLocked && (
                                <Lock className="w-4 h-4 text-gray-500" />
                              )}
                              <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600">
                                {topic.title}
                              </h3>
                            </div>
                          </div>

                          <p className="text-gray-600 mb-3 line-clamp-1">
                            {topic.content.replace(/<[^>]*>/g, '')}
                          </p>

                          {/* Tags */}
                          <div className="flex flex-wrap gap-2 mb-3">
                          <Badge className={categoryInfo.color}>
                              {(() => {
                                const IconComponent = categoryInfo.icon;
                                return <IconComponent className="w-4 h-4" />;
                              })()}
                              <span className="ml-1">{categoryInfo.dictLabel}</span>
                            </Badge>
                            {topic?.tags?.replace(/，/g, ',')  // 将中文逗号替换为英文逗号
                                .split(',')
                                .filter(tag => tag.trim()) // 过滤空标签
                                .map((tag, tagIndex) => (
                                    <Badge key={`${topic.id}-tag-${tagIndex}`} variant="outline" className="text-xs">
                                      {tag.trim()}
                                    </Badge>
                                ))
                            }
                          </div>

                          {/* Meta Info */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <div className="flex items-center space-x-1">
                                <span>{topic.nickname}</span>
                                <Badge className={`text-xs ${getRoleColor(topic.userRole)}`}>
                                  {topic.userRole === 1 ? '用户' : '官方' }
                                </Badge>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{topic.createTime}</span>
                              </div>
                            </div>

                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <div className="flex items-center space-x-1">
                                <Star className="w-4 h-4" />
                                <span>{topic.collectCount}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <MessageSquare className="w-4 h-4" />
                                <span>{topic.commentCount}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Eye className="w-4 h-4" />
                                <span>{topic.readCount}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {/* 加载更多指示器 */}
            <div ref={listEndRef} className="mt-6 text-center">
              {topicsList.length > 0 && hasMore && (
                <>
                  <Button 
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    variant="outline"
                    className="w-full py-6"
                  >
                    {isLoadingMore ? (
                      <>
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                        加载中...
                      </>
                    ) : (
                      '加载更多'
                    )}
                  </Button>
                  <p className="text-sm text-gray-500 mt-2">
                    已加载 {topicsList.length} / {total} 条
                  </p>
                </>
              )}
              
              {!hasMore && topicsList.length > 0 && (
                <p className="text-sm text-gray-500 py-4">
                  已经到底了 ~
                </p>
              )}
            </div>

            {/* 空状态 - 无搜索结果 */}
            {topicsList.length === 0 && !isLoading && !hasError && debouncedSearchTerm && (
              <div className="text-center py-12">
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">未找到相关讨论</h3>
                <p className="text-gray-500 mb-4">
                  没有找到与 "<span className="font-medium">{debouncedSearchTerm}</span>" 相关的内容
                </p>
                <div className="flex justify-center gap-3">
                  <Button variant="outline" onClick={() => setSearchTerm("")}>
                    清空搜索
                  </Button>
                  <Button onClick={handleCreateTopic}>
                    发起讨论
                  </Button>
                </div>
              </div>
            )}
            
            {/* 空状态 - 无数据 */}
            {topicsList.length === 0 && !isLoading && !hasError && !debouncedSearchTerm && (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">暂无相关讨论</h3>
                <p className="text-gray-500 mb-4">
                  成为第一个在这个分类下发起讨论的人
                </p>
                <Button onClick={handleCreateTopic}>
                  发起讨论
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            欢迎加入光智云社区
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            与全球光学仿真专家一起交流学习，共同推进技术发展
          </p>
          {!isAuthenticated && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild>
                  <Link to="/trial">
                    立即注册 <ArrowRight className="ml-2 w-4 h-4"/>
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/contact">
                    了解更多
                  </Link>
                </Button>
              </div>
          )}
        </div>
      </section>

      {/* 登录弹窗 */}
      <LoginDialog
          isOpen={isLoginDialogOpen}
          onClose={() => setIsLoginDialogOpen(false)}
          onLoginSuccess={handleLoginSuccess}
      />

      {/* 发起讨论弹窗 */}
      <CreateTopicDialog
          isOpen={isCreateTopicOpen}
          onClose={() => setIsCreateTopicOpen(false)}
          onSuccess={fetchTopicList}
      />
    </Layout>
  );
};

export default Community;